import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { useRadioGroup, VStack, Stack, Radio, Button, Box, Text, Flex, Image } from '@chakra-ui/react'
import { useTranslation } from 'react-i18next'
import { DefaultCheckbox } from '../checkboxes/default-checkbox'
import { selectScenario, useSelector } from '../../state'
import { getCookie } from '../../scenarios'
import { mapProvinceFromSlug } from '../../scenarios/utils'
import { cardImageMap } from '../../state/interfaces'

interface Choice {
  value: string
  label: string
}

export interface RadioButtonDefaultProps {
  name: string
  choices: Choice[]
  onChange?: (values: string[]) => void
  onConfirm?: (values: string[]) => void
  grandImage?: string
}

export const RadioButtonDefault: FC<RadioButtonDefaultProps> = ({ choices, onConfirm, name, grandImage }) => {
  const optionsDemandingVerificationOfProvince = ['cant-find-offer', 'price-wrong']
  const [selectedChoice, setSelectedChoice] = useState<string>('')
  const [needsProvinceVerification, setNeedsProvinceVerification] = useState(false)
  const [showProvinceVerificationWarning, setShowProvinceVerificationWarning] = useState(false)
  const [confirmProvinceVerification, setConfirmProvinceVerification] = useState(false)
  const [isButtonDisabled, setIsButtonDisabled] = useState(true)
  const image = useMemo(() => (grandImage ? cardImageMap[grandImage] : ''), [grandImage])
  const { getRadioProps, getRootProps } = useRadioGroup({
    onChange: (value) => {
      setSelectedChoice(value)
      if (needsProvinceVerification) {
        setShowProvinceVerificationWarning(optionsDemandingVerificationOfProvince.includes(value))
      } else {
        setShowProvinceVerificationWarning(false)
      }
    },
    name,
  })
  const { t } = useTranslation()

  const confirmChoices = useCallback(() => {
    onConfirm && onConfirm([selectedChoice])
  }, [onConfirm, selectedChoice])

  const handleCheckboxChange = (value: string, checked: boolean) => {
    setConfirmProvinceVerification(checked)
  }
  const scenario = useSelector(selectScenario)
  const scenarioAdditionalInformation = scenario?.initialInformation

  useEffect(() => {
    if (scenarioAdditionalInformation) {
      const provinceVerificationNeeded = scenarioAdditionalInformation?.provider as boolean
      if (provinceVerificationNeeded) {
        setNeedsProvinceVerification(provinceVerificationNeeded)
      }
    }
  }, [])

  useEffect(() => {
    if (selectedChoice === '') {
      setIsButtonDisabled(true)
    } else {
      if (showProvinceVerificationWarning) {
        if (confirmProvinceVerification) {
          setIsButtonDisabled(false)
        } else {
          setIsButtonDisabled(true)
        }
      } else {
        setIsButtonDisabled(false)
      }
    }
  }, [selectedChoice, showProvinceVerificationWarning, confirmProvinceVerification])

  return (
    <VStack mt={6}>
      <Box>
        <Flex>
          <Image src={image} alt="image" width={['0', '180px']} height={['0', '180px']} mr={4} />
          <Stack {...getRootProps()} width="100%" justifyContent="flex-start" flexWrap="wrap" margin="2vh">
            {choices.map((props) => (
              <label key={`choice-${props.value}`}>
                <Flex>
                  <Radio
                    {...getRadioProps({ value: props.value })}
                    key={`choice-${props.value}`}
                    colorScheme="green"
                    fontSize="xs"
                  />
                  <Text fontSize={{ base: '16', md: '24' }}>{props.label}</Text>
                </Flex>
              </label>
            ))}
          </Stack>
        </Flex>
      </Box>
      <Flex alignItems={'center'} direction={'column'} maxW={'600px'}>
        {showProvinceVerificationWarning && (
          <Box mb={6}>
            <DefaultCheckbox
              choice={{
                label:
                  selectedChoice === 'price-wrong'
                    ? t('confirm-province-price', {
                        providerName: scenarioAdditionalInformation?.providerName,
                        province: mapProvinceFromSlug(getCookie('province')),
                      })
                    : t('confirm-province-offer', {
                        providerName: scenarioAdditionalInformation?.providerName,
                        province: mapProvinceFromSlug(getCookie('province')),
                      }),
                value: 'province-verified',
              }}
              isChecked={confirmProvinceVerification}
              onChange={handleCheckboxChange}
            />
          </Box>
        )}
        <Button
          onClick={confirmChoices}
          disabled={isButtonDisabled}
          variant="solid"
          color="white"
          backgroundColor="green.600"
          fontSize="xs"
          fontWeight="bold"
          borderWidth="2px"
          borderColor="green.600"
          px={16}
          py={6}
          _focus={{
            borderColor: 'brand',
            boxShadow: '0 0 0 2px #5bce4c',
          }}
        >
          {t('common_continue')}
        </Button>
      </Flex>
    </VStack>
  )
}
